import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { Tooltip } from "primereact/tooltip";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import { goToTop } from "../../../../../Components/goToTop";
import Textarea from "../../../../../Components/TextArea";
import { documentFileExtension, imageFileExtension } from "../../../../../Api/FileConstant";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";
import { formatDate } from "../../../../../Utils/getDate";
import TextInput from "../../../../../Components/TextInput";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";

import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { checkText } from "../../../../../Utils/Validator";

import {
    SiteVisit as SiteVisitType,
    SiteVisitNoteList,
    SiteVisitInspectionList,
    SiteVisitBuildingInspectorResponse,
    ResponseFileList,
    SiteVisitArchitectResponse,
    SiteVisitReference,
} from "../../../../../Controllers/Types/SiteVisit/SiteVisitTypes";

import { getAllSiteVisitList } from "../../../../../Controllers/Thunks/SiteVisitThunks";

import {
    createSiteVisitNote,
    addNoteReferenceList,
    updateSiteVisitNote,
    deleteSiteVisitNote,
    deleteSiteVisitNoteFile,
    siteVisitResponseByArchitect,
    deleteArchitectResponse,
    deleteArcResponseFile,
    sendSiteVisitReportViaEmail
} from "../../../../../Api/SiteVisit/SiteVisitApiServices";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import FileActionLoader from "../../../../../Components/FileActionLoader";

interface EmailInput {
    value: string;
    error: string;
}

const SiteVisitDetail: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [addLoading, setAddLoading] = useState<boolean>(false);
    const today = new Date();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const dispatch = useAppDispatch();

    const isArchitect = (): boolean => {
        return (loginUserRoleName.toLocaleLowerCase() === 'architect' || loginUserRoleName.toLocaleLowerCase() === 'multi-displinary');
    };


    const isBuildingInspector = (): boolean => {
        return (loginUserRoleName.toLocaleLowerCase() === 'building inspector' || loginUserRoleName.toLocaleLowerCase() === 'area manager' || loginUserRoleName.toLocaleLowerCase() === 'building inspector company' || loginUserRoleName.toLocaleLowerCase() === 'plan check engineer');
    };


    const siteVisitLoading = useAppSelector((state) => state?.siteVisitList?.siteVisitLoading);
    const siteVisitError = useAppSelector((state) => state?.siteVisitList?.siteVisitError);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [noteFileDeleteLoadingId, setNoteFileDeleteLoadingId] = useState<number | null>(null);
    const [responseFileDeleteLoadingId, setResponseFileDeleteLoadingId] = useState<number | null>(null);

    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const projectData = location.state?.projectData;
    const folderId = Number(projectData.folder_id);

    const [siteVisitData, setSiteVisitData] = useState<SiteVisitType | null>(null);
    const [isSignedReportByBi, setIsSignedReportByBi] = useState<boolean>(false);

    /* Pagination */
    const [first, setFirst] = useState<number>(0);
    const [rows, setRows] = useState<number>(10);

    /* Inspections */
    const [siteInspectionData, setSiteInspectionData] = useState<SiteVisitInspectionList[]>([]);
    const [paginatedSiteInspectionData, setPaginatedSiteInspectionData] = useState<SiteVisitInspectionList[]>([]);
    const [selectedSiteInspectionData, setSelectedSiteInspectionData] = useState<SiteVisitInspectionList | null>(null);
    const [activeSiteInspectionData, setActiveSiteInspectionData] = useState<SiteVisitInspectionList | null>(null);

    /* Notes */
    const [selectedNoteData, setSelectedNoteData] = useState<SiteVisitNoteList | null>(null);
    const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
    const [noteNumber, setNoteNumber] = useState<string>("");
    const [note, setNote] = useState<string>('');
    const [noteStatus, setNoteStatus] = useState<'Satisfactory' | 'Unsatisfactory'>('Unsatisfactory');
    const [noteFiles, setNoteFiles] = useState<File[]>([]);

    const [noteNumberError, setNoteNumberError] = useState<string>("");
    const [noteError, setNoteError] = useState<string>('');
    const [noteStatusError, setNoteStatusError] = useState<string>('');
    const [noteFilesError, setNoteFilesError] = useState<string>('');
    const [noteUpdateHasChanges, setNoteUpdateHasChanges] = useState<boolean>(false);

    /* For architect response */
    const [showArchitectResponseModal, setShowArchitectResponseModal] = useState<boolean>(false);
    const [arcResponseNote, setArcResponseNote] = useState<string>('');
    const [arcResponseFiles, setArcResponseFiles] = useState<File[]>([]);

    const [arcResponseNoteError, setArcResponseNoteError] = useState<string>('');
    const [arcResponseFilesError, setArcResponseFilesError] = useState<string>('');
    const [arcResponseHasChanges, setArcResponseHasChanges] = useState<boolean>(false);

    /* For reference */
    const [referenceData, setReferenceData] = useState<SiteVisitReference[]>([]);
    const [selectedReferenceData, setselectedReferenceData] = useState<SiteVisitReference | null>(null);
    const [showReferenceModal, setShowReferenceModal] = useState<boolean>(false);
    const [selectedReferenceList, setSelectedReferenceList] = useState<SiteVisitReference[]>([]);
    const [showMultipleSelection, setShowMultipleSelection] = useState<boolean>(false);

    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showNoteDocViewModal, setShowNoteDocViewModal] = useState<boolean>(false);

    /* For send email */
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddresses, setEmailAddresses] = useState<EmailInput[]>([
        { value: '', error: '' },
    ]);

    /* Download */
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const havePermissionDownloadSiteVisitReport = useCanEdit("Site Visit", "dwonload_site_visit");
    const havePermissionShareSiteVisit = useCanEdit("Site Visit", "share_site_visit");
    const havePermissionAddSiteVisitQuestion = useCanEdit("Site Visit", "add_site_visit_question");
    const havePermissionAddSiteVisitNote = useCanEdit("Site Visit", "add_site_visit_note");
    const havePermissionEditSiteVisitNote = useCanEdit("Site Visit", "edit_site_visit_note");
    const havePermissionDeleteSiteVisitNote = useCanEdit("Site Visit", "delete_site_visit_note");
    /*  */

    // Get logged in user role name
    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    // Fetch data initially from state
    useEffect(() => {
        siteVisitDataGet();
    }, [location?.state]);

    const siteVisitDataGet = async () => {
        await dispatch(getAllSiteVisitList(folderId));
        setInitialDataLoading(false);
    }

    // get the data from state
    const siteVisitList: SiteVisitType[] = useAppSelector((state) => state?.siteVisitList?.siteVisitList);

    useEffect(() => {
        if (location?.state && location?.state.siteVisitData) {
            const hasData = siteVisitList && siteVisitList.length > 0;
            const stateData = location?.state?.siteVisitData as SiteVisitType;

            if (hasData) {
                const data = siteVisitList.filter((siteVisit) => siteVisit.id === stateData.id);
                if (data && data.length > 0) {
                    setSiteVisitData(data[0]);
                } else {
                    setSiteVisitData(null);
                }
            } else {
                setSiteVisitData(null);
            }
        }
    }, [location?.state, siteVisitList]);


    useEffect(() => {
        if (siteVisitData && siteVisitData !== null) {
            if (siteVisitData?.sign_report === 1) {
                setIsSignedReportByBi(true);
            } else {
                setIsSignedReportByBi(false);
            }
        } else {
            setIsSignedReportByBi(false);
        }
    }, [siteVisitData]);

    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setFirst(event.first);
        setRows(event.rows);
    };


    useEffect(() => {
        if (siteVisitData && siteVisitData !== null) {
            if (siteVisitData?.siteVisitInspectionList && siteVisitData?.siteVisitInspectionList?.length > 0) {
                setSiteInspectionData(siteVisitData?.siteVisitInspectionList);
            } else {
                setSiteInspectionData([]);
            }
        } else {
            setSiteInspectionData([]);
        }
    }, [siteVisitData]);


    useEffect(() => {
        const paginatedData = siteInspectionData.slice(first, first + rows);
        setPaginatedSiteInspectionData(paginatedData);
    }, [siteInspectionData, first, rows]);


    useEffect(() => {
        if (paginatedSiteInspectionData && paginatedSiteInspectionData?.length > 0) {
            if (selectedSiteInspectionData && selectedSiteInspectionData !== null) {
                setActiveSiteInspectionData(selectedSiteInspectionData);
            } else {
                setActiveSiteInspectionData(paginatedSiteInspectionData?.[0]);
            }
        } else {
            setActiveSiteInspectionData(null);
        }
    }, [paginatedSiteInspectionData, selectedSiteInspectionData]);


    /* ********************************* Inspction *********************************** */
    const handleToggleSiteInspection = (data: SiteVisitInspectionList) => {
        setActiveSiteInspectionData((prevSelected) =>
            prevSelected?.id === data.id ? null : data
        );
        goToTop();
    };

    const siteInspectionNoteCountBody = (data: SiteVisitInspectionList) => {
        const totalNotesCount = data?.note_list?.length;
        const satisfiedNotesCount = data?.note_list?.filter(note => note?.status === 'Satisfactory')?.length;

        return (
            <div className={`item_count_lable primary`}>
                <span>
                    {String(satisfiedNotesCount).padStart(2, '0') + '/' + String(totalNotesCount).padStart(2, '0')}
                </span>
            </div>
        );
    };

    const tableActionBody = (data: SiteVisitInspectionList) => {
        return (
            <div className="table_action_btn_area mt-1 mb-1">
                {havePermissionAddSiteVisitNote && (
                    <button
                        className={`table_action_btn white p-ripple`}
                        onClick={(e) => {
                            handleAddNote(data);
                            setActiveSiteInspectionData(data);
                            e.stopPropagation();
                        }}>
                        Add Note
                        <Ripple />
                    </button>
                )}
            </div>
        );
    };
    /* *************************************************************************** */



    /* ********************************* Notes *********************************** */
    const handleCloseNoteModal = () => {
        setShowNoteModal(false);

        handleResetNoteFields();
        handleResetNoteErrors();
    }

    const handleResetNoteFields = () => {
        setSelectedNoteData(null);
        setNote('');
        setNoteFiles([]);
        setDataState('Add');
        setNoteStatus('Unsatisfactory');

        setselectedReferenceData(null);
    }

    const handleResetNoteErrors = () => {
        setNoteError('');
        setNoteFilesError('');
        setNoteStatusError('');
        setNoteNumberError('');
    }

    const handleNoteFilesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setNoteFiles((prev) => [...prev, ...filesArray]);
        }
    }

    const handleRemoveNoteFile = (index: number) => {
        setNoteFiles((prev) => prev.filter((_, i) => i !== index));
    }

    useEffect(() => {
        if (dataState === 'Add' && activeSiteInspectionData && activeSiteInspectionData !== null && siteVisitData !== null) {
            generateNoteNumber(activeSiteInspectionData);
        }
    }, [dataState, activeSiteInspectionData, siteVisitData]);

    const generateNoteNumber = (inspectionData: SiteVisitInspectionList) => {
        let noteNo = 0;

        if (inspectionData && inspectionData !== null) {
            const currentInspectionData = siteVisitData?.siteVisitInspectionList?.find(data => data?.id === inspectionData?.id)
            noteNo = currentInspectionData?.note_list?.length! + 1;
        }

        setNoteNumber(noteNo.toString());
    }

    // validations for note.
    function checkCredentialsForNote(): boolean {
        const note_number = checkText(noteNumber, true, setNoteNumberError);
        const inspection_note = checkText(note, true, setNoteError);

        return note_number && inspection_note;
    }

    // clear message
    useEffect(() => {
        checkText(noteNumber, false, setNoteNumberError);
    }, [noteNumber]);

    useEffect(() => {
        checkText(note, false, setNoteError);
    }, [note]);

    useEffect(() => {
        if (noteStatus) {
            setNoteStatusError('');
        }
    }, [noteStatus]);

    useEffect(() => {
        if (noteFiles) {
            setNoteFilesError('');
        }
    }, [noteFiles]);

    // handle api error
    function showAPIErrorForNote(keyName: string, error: string): void {
        if (keyName === "note_no") {
            setNoteNumberError(error);
        }

        if (keyName === "note") {
            setNoteError(error);
        }

        if (keyName === "status") {
            setNoteStatusError(error);
        }

        if (keyName === "file_list[]") {
            setNoteFilesError(error);
        }
    }

    const handleAddNote = (inspectionData: SiteVisitInspectionList) => {
        if (inspectionData) {
            const references: SiteVisitReference[] = inspectionData?.site_visit_inspection?.reference || [];

            setSelectedSiteInspectionData(inspectionData);
            setShowNoteModal(true);
            setDataState('Add');
            generateNoteNumber(inspectionData);

            if (references && references?.length > 0) {
                setReferenceData(references);
            } else {
                setReferenceData([]);
            }
        }
    }

    const handleCreateNote = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsForNote()) {
            setLoading(true);

            const site_visit_id: number = siteVisitData?.id!;
            const inspection_list_id: number = selectedSiteInspectionData?.id!;
            const reference_id: number | null = selectedReferenceData?.id || null;
            const note_no: string = noteNumber!;
            const inspection_note: string = note!;
            const status: string = noteStatus!;
            const file_list: File[] = noteFiles!;

            const data = await createSiteVisitNote(
                site_visit_id,
                inspection_list_id,
                reference_id,
                note_no,
                inspection_note,
                status,
                file_list
            )

            setLoading(false);

            if (data?.statusCode === 200) {
                siteVisitDataGet();
                handleCloseNoteModal();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Note added successfully.',
                        life: 3000
                    });
                }
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForNote(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating the note.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleToggleAddMultipleQuestions = () => {
        setShowMultipleSelection(!showMultipleSelection);
        setSelectedReferenceList([]);
    }

    const addMultipleQuestions = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedReferenceList?.length === 0) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Warning',
                    detail: 'Please select the questions from the reference.',
                    life: 3000
                });
            }
            return;
        }

        setAddLoading(true);

        const site_visit_id: number = siteVisitData?.id!;
        const inspection_list_id: number = selectedSiteInspectionData?.id!;
        const reference_list: SiteVisitReference[] = selectedReferenceList || [];

        const data = await addNoteReferenceList(
            site_visit_id,
            inspection_list_id,
            reference_list
        );

        setAddLoading(false);

        if (data?.statusCode === 200) {
            siteVisitDataGet();
            handleCloseNoteModal();
            handleCloseReferenceModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'The selected questions were successfully added to the note.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error adding the selected questions to the note.',
                    life: 3000
                });
            }
        }
    }

    useEffect(() => {
        if (selectedNoteData && selectedNoteData !== null) {
            if (note !== selectedNoteData?.note) {
                setNoteUpdateHasChanges(true);
            } else if (noteStatus !== selectedNoteData?.status) {
                setNoteUpdateHasChanges(true);
            } else if (noteFiles && noteFiles?.length !== 0) {
                setNoteUpdateHasChanges(true);
            } else {
                setNoteUpdateHasChanges(false);
            }
        }
    }, [selectedNoteData, note, noteStatus, noteFiles]);

    const handleEditNote = (note: SiteVisitNoteList, inspection: SiteVisitInspectionList) => {
        if (inspection) {
            setSelectedSiteInspectionData(inspection);

            if (note) {
                setShowNoteModal(true);
                setDataState('Edit');

                setNote(note?.note || '');
                setNoteStatus(note?.status);
                setNoteNumber(String(note?.note_no));

                setSelectedNoteData(note);
            } else {
                setSelectedNoteData(null);
            }
        }
    }

    const handleUpdateNote = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsForNote()) {
            setLoading(true);

            const note_id: number = selectedNoteData?.id!;
            const site_visit_id: number = siteVisitData?.id!;
            const reference_id: number | null = selectedNoteData?.reference_id!;
            const inspection_note: string = note!;
            const status: string = noteStatus!;
            const file_list: File[] = noteFiles!;

            const data = await updateSiteVisitNote(
                note_id,
                site_visit_id,
                reference_id,
                inspection_note,
                status,
                file_list
            )

            setLoading(false);

            if (data?.statusCode === 200) {
                siteVisitDataGet();
                handleCloseNoteModal();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Note updated successfully.',
                        life: 3000
                    });
                }
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForNote(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the note.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleDeleteNote = (): void => {
        confirmDialog({
            message: 'Are you sure you want to delete this note?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteNote(selectedNoteData?.id!),
        });
    }

    const deleteNote = async (noteId: number) => {
        setDeleteLoading(true);
        const result = await deleteSiteVisitNote(noteId);

        setDeleteLoading(false);
        if (result.statusCode === 200) {
            siteVisitDataGet();
            handleCloseNoteModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Note deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the note.',
                    life: 3000
                });
            }
        }
    }

    // update the state after file delete
    useEffect(() => {
        if (siteVisitData && siteVisitData !== null) {
            if (dataState === 'Edit' && selectedNoteData && selectedNoteData !== null && activeSiteInspectionData && activeSiteInspectionData !== null) {
                const currentInspection = siteVisitData?.siteVisitInspectionList?.find(inspection => inspection?.id === activeSiteInspectionData?.id);

                if (currentInspection !== null) {
                    const updatedNoteData = currentInspection?.note_list?.find(note => note?.id === selectedNoteData?.id);
                    setSelectedNoteData(updatedNoteData || null);
                }
            }
        }

    }, [dataState, selectedNoteData, activeSiteInspectionData, siteVisitData]);

    const handleDeleteNoteFile = (noteFileId: number) => {
        confirmDialog({
            message: 'Are you sure you want to delete this uploaded file?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteNoteFile(noteFileId),
        });
    }

    const deleteNoteFile = async (noteFileId: number) => {
        setNoteFileDeleteLoadingId(noteFileId);
        const result = await deleteSiteVisitNoteFile(noteFileId);

        setNoteFileDeleteLoadingId(null);

        if (result.statusCode === 200) {
            siteVisitDataGet();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the file.',
                    life: 3000
                });
            }
        }
    }

    const noteModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} note
            </h5>

            {dataState === 'Edit' ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <React.Fragment>
                            {havePermissionDeleteSiteVisitNote && (
                                <button type="button" className="close_modal_btn p-ripple"
                                    onClick={handleDeleteNote}>
                                    <i className="bi bi-trash3"></i>
                                    <Ripple />
                                </button>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <button type="button" className="close_modal_btn p-ripple"
                    onClick={handleCloseNoteModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    )

    const noteModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseNoteModal}
            />

            <Button
                loading={loading}
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}`}
                className="custom_btn primary"
                onClick={dataState === 'Add' ? handleCreateNote : handleUpdateNote}
                disabled={dataState === 'Edit' && !noteUpdateHasChanges}
            />
        </div>
    )
    /* *************************************************************************** */


    /* ***************************** Note reference ****************************** */
    const handleCloseReferenceModal = () => {
        setShowReferenceModal(false);

        setSelectedReferenceList([]);
        setShowMultipleSelection(false);
    }

    const handleAddReference = () => {
        setShowReferenceModal(true);
    }

    const handleSelectReference = (reference: SiteVisitReference) => {
        setNote(reference?.inspection_reference!);

        setTimeout(() => {
            setselectedReferenceData(reference);
            handleCloseReferenceModal();
        }, 300);
    }

    const referenceModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                References for {selectedSiteInspectionData?.inspection_name}
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseReferenceModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const referenceModalFooter = (
        <div className="custom_modal_footer">
            <Button
                loading={addLoading}
                label={`${addLoading ? 'Processing' : `Add selected references (${selectedReferenceList?.length})`}`}
                className="custom_btn primary"
                onClick={addMultipleQuestions}
                disabled={selectedReferenceList?.length === 0}
            />
        </div>
    )
    /* *************************************************************************** */


    /* *************************** Architect response **************************** */
    const handeleCloseArcResponseModal = () => {
        setShowArchitectResponseModal(false);
        setSelectedNoteData(null);
        handleResetArcResponseFields();
        handleResetArcResponseErrors();
        setDataState('Add');
    }

    const handleResetArcResponseFields = () => {
        setArcResponseNote('');
        setArcResponseFiles([]);
    }

    const handleResetArcResponseErrors = () => {
        setArcResponseNoteError('');
        setArcResponseFilesError('');
    }

    // validations for response.
    function checkCredentialsArcResponse(): boolean {
        const response_note = checkText(arcResponseNote, true, setArcResponseNoteError);

        return response_note;
    }

    // clear message
    useEffect(() => {
        checkText(arcResponseNote, false, setArcResponseNoteError);
    }, [arcResponseNote]);

    // handle api error
    function showAPIErrorArcResponse(keyName: string, error: string): void {
        if (keyName === "response") {
            setArcResponseNoteError(error);
        }
    }

    /* Add */
    const handleAddArcResponse = (data: SiteVisitNoteList, inspection: SiteVisitInspectionList) => {
        if (inspection) {
            setSelectedSiteInspectionData(inspection);

            if (data) {
                setDataState('Add');
                setShowArchitectResponseModal(true);
                setSelectedNoteData(data);

                handleResetArcResponseFields();
                handleResetArcResponseErrors();
            }
        }
    }

    /* Create */
    const handleResponseArchitect = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsArcResponse()) {
            setLoading(true);

            const site_visit_id: number = siteVisitData?.id!;
            const note_id: number = selectedNoteData?.id!;
            const note_response: string = arcResponseNote!;
            const date: string = formatDate(today!);
            const file_list: File[] = arcResponseFiles || [];

            const data = await siteVisitResponseByArchitect(
                site_visit_id,
                note_id,
                note_response,
                date,
                file_list
            )

            setLoading(false);

            if (data?.statusCode === 200) {
                siteVisitDataGet();
                handeleCloseArcResponseModal();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: `Response ${dataState === 'Add' ? 'saved' : 'updated'} successfully.`,
                        life: 3000
                    });
                }
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorArcResponse(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: `There was an error ${dataState === 'Add' ? 'creating' : 'updating'} the response.`,
                        life: 3000
                    });
                }
            }
        }
    }

    useEffect(() => {
        if (selectedNoteData && selectedNoteData !== null) {
            if (arcResponseNote !== selectedNoteData?.response_architect?.response) {
                setArcResponseHasChanges(true);
            } else if (arcResponseFiles && arcResponseFiles?.length !== 0) {
                setArcResponseHasChanges(true);
            } else {
                setArcResponseHasChanges(false);
            }
        }
    }, [selectedNoteData, arcResponseNote, arcResponseFiles]);

    /* Edit */
    const handleEditArcResponse = (data: SiteVisitNoteList, inspection: SiteVisitInspectionList) => {
        if (inspection) {
            setSelectedSiteInspectionData(inspection);

            if (data) {
                setDataState('Edit');
                setShowArchitectResponseModal(true);
                setSelectedNoteData(data);

                setArcResponseNote(data?.response_architect?.response!);
            }
        }
    }

    const handleDeleteResponse = (): void => {
        confirmDialog({
            message: 'Are you sure you want to delete this response?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteResponse(selectedNoteData?.id!),
        });
    }

    const deleteResponse = async (noteId: number) => {
        const result = await deleteArchitectResponse(noteId);

        if (result.statusCode === 200) {
            siteVisitDataGet();
            handeleCloseArcResponseModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Response deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the response.',
                    life: 3000
                });
            }
        }
    }

    const responseModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} Response
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handeleCloseArcResponseModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const responseModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handeleCloseArcResponseModal}
            />

            <Button
                loading={loading}
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}`}
                className="custom_btn primary"
                onClick={handleResponseArchitect}
                disabled={dataState === 'Edit' && !arcResponseHasChanges}
            />
        </div>
    )

    /* For response files */
    const handleResponseFilesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setArcResponseFiles((prev) => [...prev, ...filesArray]);
        }
    }

    const handleRemoveResponseFile = (index: number) => {
        setArcResponseFiles((prev) => prev.filter((_, i) => i !== index));
    }

    const handleDeleteResponseFile = (resFileId: number) => {
        confirmDialog({
            message: 'Are you sure you want to delete this uploaded file?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteResponseFile(resFileId),
        });
    }

    const deleteResponseFile = async (resFileId: number) => {
        setResponseFileDeleteLoadingId(resFileId);
        const result = await deleteArcResponseFile(resFileId);

        setResponseFileDeleteLoadingId(null);

        if (result.statusCode === 200) {
            siteVisitDataGet();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File deleted successfully.',
                    life: 3000
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the file.',
                    life: 3000
                });
            }
        }
    }
    /* *************************************************************************** */


    /* ************************* Site visit detail common ************************* */
    const handlePreviewReport = () => {
        navigate(`/project/detail/default-service/onsite/site-visit/report`,
            { state: { projectData: projectData, siteVisitData: siteVisitData } }
        );
        goToTop();
    }
    /* *************************************************************************** */


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = baseURL +"/"+ siteVisitData?.file_download_path;
        const reportPath = siteVisitData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "Site visit report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };

    const handleViewReportDocument = () => {
        if (siteVisitData?.file_download_path) {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(siteVisitData?.file_download_path);
        }
    };
    /* *************************************************************************** */



    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddresses([{ value: '', error: '' }]);
    }

    const handleSendEmail = () => {
        setShowMailModal(true);
    }

    const validateEmail = (email: string): string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required.';
        if (!emailRegex.test(email)) return 'Enter a valid email.';
        return '';
    };

    // validations.
    function checkCredentialsEmail(): boolean {
        const updatedEmails = emailAddresses.map((email) => {
            const error = validateEmail(email.value);
            return { ...email, error };
        });

        setEmailAddresses(updatedEmails);

        const hasErrors = updatedEmails.some(email => email.error !== '');
        return !hasErrors;
    }

    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email_list[]") {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    const sendEMail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail()) return;

        setLoading(true);

        const site_visit_id: number = siteVisitData?.id!;
        const email_list: string[] = emailAddresses.map((email) => email.value);

        const data = await sendSiteVisitReportViaEmail(
            site_visit_id,
            email_list
        );

        setLoading(false);

        if (data.statusCode === 200) {
            siteVisitDataGet();
            handleCloseMailModal();

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Email sent successfully.",
                    life: 3000,
                });
            }
        } else if (data.statusCode === 422) {
            Object.entries(data.errors ?? []).map(([key, value], index) =>
                showAPIErrorEmail(key, value[0])
            );
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error sending the email.",
                    life: 3000,
                });
            }
        }
    }

    const handleAddMail = () => {
        const lastEmail = emailAddresses[emailAddresses.length - 1];
        const validationMessage = validateEmail(lastEmail.value);

        if (validationMessage) {
            const updatedEmails = [...emailAddresses];
            updatedEmails[updatedEmails.length - 1].error = validationMessage;
            setEmailAddresses(updatedEmails);
            return;
        }

        setEmailAddresses([...emailAddresses, { value: '', error: '' }]);
    };

    const handleRemoveMail = (index: number) => {
        if (emailAddresses.length === 1) return;
        const updatedEmails = emailAddresses.filter((_, i) => i !== index);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index: number, newValue: string) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = newValue;
        updatedEmails[index].error = '';
        setEmailAddresses(updatedEmails);
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Send report via mail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? 'Sending' : 'Send'}`}
                className="custom_btn primary"
                onClick={sendEMail}
            />
        </div>
    )
    /* *************************************************************************** */

    const handleCloseFileViewModal = () => {
        setShowNoteDocViewModal(false);
    };

    const handleViewDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };

    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */


    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {siteVisitLoading && initialDataLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : siteVisitError ? (
                    <ErrorPage errorMessage={siteVisitError} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.feature_name}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    Site visit {siteVisitData && siteVisitData?.site_visit_no}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3 justify-content-end">
                            <div className="page_action_area">
                                {siteVisitData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {havePermissionDownloadSiteVisitReport && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {havePermissionShareSiteVisit && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSendEmail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                <button
                                    className="add_data_link_btn p-ripple"
                                    onClick={handlePreviewReport}>
                                    <i className="bi bi-file-earmark-text"></i>
                                    <span>Preview report</span>
                                    <Ripple />
                                </button>
                            </div>
                        </div>

                        <div className="page_main_content_section no_header">
                            {paginatedSiteInspectionData && paginatedSiteInspectionData?.length > 0 ? (
                                <div className="pck_track_list_area">
                                    {siteVisitLoading && (
                                        <CustomTableLoader />
                                    )}

                                    <div className="table-responsive pck_track_list_table_area">
                                        <table className="table pck_track_list_table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "4%" }} className="text_no_wrap"></th>

                                                    <th style={{ width: (isSignedReportByBi === false && isBuildingInspector()) ? "76%" : "86%" }} className="text_no_wrap">
                                                        Inspection
                                                    </th>

                                                    <th style={{ width: "10%" }}></th>

                                                    {(isSignedReportByBi === false && isBuildingInspector()) && (
                                                        <th style={{ width: "10%" }}></th>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {paginatedSiteInspectionData?.map((inspection) => {
                                                    const isActive = inspection?.id === activeSiteInspectionData?.id;

                                                    return (
                                                        <React.Fragment>
                                                            <tr
                                                                key={`inspection_${inspection?.id}`}
                                                                onClick={() => handleToggleSiteInspection(inspection)}
                                                                className={`part_row ${isActive ? "active" : ""}`}
                                                            >
                                                                <td
                                                                    style={{ width: "3rem" }}
                                                                    className="text-center">
                                                                    <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                </td>

                                                                <td
                                                                    style={{ width: (isSignedReportByBi === false && isBuildingInspector()) ? "76%" : "86%" }}
                                                                    className="note_column text_semi_bold"
                                                                >
                                                                    {inspection?.inspection_name}
                                                                </td>

                                                                <td style={{ width: "10%" }}>
                                                                    {siteInspectionNoteCountBody(inspection)}
                                                                </td>

                                                                {isSignedReportByBi === false && isBuildingInspector() && (
                                                                    <td style={{ width: "10%" }}>
                                                                        {tableActionBody(inspection)}
                                                                    </td>
                                                                )}
                                                            </tr>

                                                            <AnimatePresence initial={false}>
                                                                {isActive && (
                                                                    <tr className="part_item_row">
                                                                        <td
                                                                            colSpan={isSignedReportByBi === false && isBuildingInspector() ? 4 : 3}
                                                                            style={{ width: "100%" }}
                                                                            className="p-0">
                                                                            <motion.div
                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                animate={{ height: "auto", opacity: 1 }}
                                                                                exit={{ height: 0, opacity: 0 }}
                                                                                transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                style={{ overflow: "hidden" }}>

                                                                                <div className="item_list_wrapper">
                                                                                    {inspection?.note_list && inspection?.note_list?.length > 0 ? (
                                                                                        <React.Fragment>
                                                                                            {inspection?.note_list?.map((noteItem) => {
                                                                                                const biResponse: SiteVisitBuildingInspectorResponse | null = noteItem?.response_building_inspector || null;
                                                                                                const biResponseFiles: ResponseFileList[] = noteItem?.response_building_inspector?.file_list || [];

                                                                                                const arcResponse: SiteVisitArchitectResponse | null = noteItem?.response_architect || null;
                                                                                                const arcResponseFiles: ResponseFileList[] = noteItem?.response_architect?.file_list || [];

                                                                                                return (
                                                                                                    <div
                                                                                                        key={`inspection_${inspection?.id}_item_${noteItem?.id}`}
                                                                                                        className={`pck_track_list_item_area with_indent ${noteItem?.status === 'Unsatisfactory' ? "warning" : "success"}`}>
                                                                                                        <div className="pck_track_list_item_header">
                                                                                                            <span className="item_code">
                                                                                                                {noteItem?.note_no}.
                                                                                                            </span>

                                                                                                            <span className="item_cmt first_letter_capitalized">
                                                                                                                {noteItem?.note}
                                                                                                            </span>
                                                                                                        </div>

                                                                                                        {noteItem?.is_current_site_visit_note === 0 && (
                                                                                                            <h6 className={`last_data_info ${noteItem?.status === 'Satisfactory' ? 'success' : ''}`}>
                                                                                                                <i className="bi bi-info-circle text-dark me-1"></i>
                                                                                                                Reported during <span>Site visit {noteItem?.parent_site_visit_no}</span>
                                                                                                            </h6>
                                                                                                        )}

                                                                                                        {biResponse && biResponse !== null && (
                                                                                                            <React.Fragment>
                                                                                                                {biResponseFiles && biResponseFiles?.length > 0 && (
                                                                                                                    <div className="note_files_area">
                                                                                                                        <div className="checklist_img_container">
                                                                                                                            {biResponseFiles?.map((file, index) => (
                                                                                                                                <div
                                                                                                                                    className="note_image_container response_image_container"
                                                                                                                                    key={file?.id}
                                                                                                                                >
                                                                                                                                    {file?.extension === "pdf" ? (
                                                                                                                                        <div
                                                                                                                                            className="response_doc_file_container"
                                                                                                                                            onClick={() => handleViewDocument(file?.path!)}
                                                                                                                                        >
                                                                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                                        </div>
                                                                                                                                    ) : (
                                                                                                                                        <Image
                                                                                                                                            src={file?.path!}
                                                                                                                                            alt={`${index + 1}`}
                                                                                                                                            className="note_img response_image"
                                                                                                                                            preview
                                                                                                                                        />
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            ))}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </React.Fragment>
                                                                                                        )}

                                                                                                        <div className="response_display_area custom_padding">
                                                                                                            {arcResponse && arcResponse !== null && (
                                                                                                                <div className="response_input_area mb-md-0 readonly">
                                                                                                                    <label>Architect Response :&nbsp;</label>

                                                                                                                    <p className="first_letter_capitalized">
                                                                                                                        {arcResponse?.response ? (
                                                                                                                            arcResponse?.response
                                                                                                                        ) : (
                                                                                                                            <span className="text_empty text_italic">N/A</span>
                                                                                                                        )}
                                                                                                                    </p>

                                                                                                                    {arcResponseFiles && arcResponseFiles?.length > 0 && (
                                                                                                                        <div className="response_file_area mt-3 pb-0">
                                                                                                                            <div className="checklist_img_container">
                                                                                                                                {arcResponseFiles?.map((file, index) => (
                                                                                                                                    <div
                                                                                                                                        className="note_image_container response_image_container"
                                                                                                                                        key={file?.id}
                                                                                                                                    >
                                                                                                                                        {file?.extension === "pdf" ? (
                                                                                                                                            <div
                                                                                                                                                className="response_doc_file_container"
                                                                                                                                                onClick={() => handleViewDocument(file?.path!)}
                                                                                                                                            >
                                                                                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                                            </div>
                                                                                                                                        ) : (
                                                                                                                                            <Image
                                                                                                                                                src={file?.path!}
                                                                                                                                                alt={`${index + 1}`}
                                                                                                                                                className="note_img response_image"
                                                                                                                                                preview
                                                                                                                                            />
                                                                                                                                        )}
                                                                                                                                    </div>
                                                                                                                                ))}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>

                                                                                                        {isBuildingInspector() ? (
                                                                                                            <React.Fragment>
                                                                                                                {isSignedReportByBi === false && havePermissionEditSiteVisitNote && (
                                                                                                                    <div className="pck_track_list_item_footer">
                                                                                                                        <div className="pck_track_list_item_btn_group">
                                                                                                                            <button
                                                                                                                                className={`table_action_btn white bordered p-ripple`}
                                                                                                                                onClick={() => handleEditNote(noteItem, inspection)}
                                                                                                                            >
                                                                                                                                Edit
                                                                                                                                <Ripple />
                                                                                                                            </button>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </React.Fragment>
                                                                                                        ) : isArchitect() ? (
                                                                                                            <React.Fragment>
                                                                                                                {isSignedReportByBi && noteItem?.status === 'Unsatisfactory' && (
                                                                                                                    <div className="pck_track_list_item_footer">
                                                                                                                        <div className="pck_track_list_item_btn_group">
                                                                                                                            <button
                                                                                                                                className={`table_action_btn white ${arcResponse !== null ? 'success' : ''} bordered p-ripple`}
                                                                                                                                onClick={() => arcResponse === null ? handleAddArcResponse(noteItem, inspection) : handleEditArcResponse(noteItem, inspection)}
                                                                                                                            >
                                                                                                                                {arcResponse === null ? 'Response' : 'Responsed '}
                                                                                                                                <Ripple />
                                                                                                                            </button>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </React.Fragment>
                                                                                                        ) : null}
                                                                                                    </div>
                                                                                                )
                                                                                            })}
                                                                                        </React.Fragment>
                                                                                    ) : (
                                                                                        <div className="no_item_area">
                                                                                            <span>
                                                                                                {`No note(s) has been created. ${isBuildingInspector() ? 'To create the note click on add button.' : ''}`}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}

                                                                                </div>
                                                                            </motion.div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </AnimatePresence>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="pagination_area">
                                        <Paginator
                                            first={first}
                                            rows={rows}
                                            totalRecords={siteInspectionData?.length}
                                            rowsPerPageOptions={[10, 20, 30]}
                                            onPageChange={onPageChange}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <NoDataCreated
                                    message={`No site inspections found.`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Note modal */}
            <Dialog
                visible={showNoteModal}
                header={noteModalHeader}
                footer={noteModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseNoteModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-2 w-100">
                        {activeSiteInspectionData && activeSiteInspectionData !== null && (
                            <div className="row">
                                {/* Inspection */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <h6 className="data_label">
                                            <span className="text-dark"> Inspection for :</span>&nbsp;{activeSiteInspectionData?.inspection_name}
                                        </h6>
                                    </div>
                                </div>

                                {/* Item code */}
                                <div className="col-12 col-sm-6">
                                    <div className="page_form_group">
                                        <label htmlFor="itemCode" className="custom_form_label">
                                            Note number&nbsp;
                                            <small>
                                                {dataState === "Add"
                                                    ? "(Auto generated)"
                                                    : "(Can't modify)"}
                                            </small>
                                        </label>
                                        <InputText
                                            key={`noteNumber`}
                                            id="noteNumber"
                                            value={String(noteNumber).padStart(2, '0')}
                                            className="custom_form_input"
                                            placeholder="Eg: 01"
                                            disabled
                                        />

                                        {noteNumberError && (
                                            <small className="form_error_msg">
                                                {noteNumberError}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                {/* Note */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <div className="form_label_grp">
                                            <label htmlFor="note" className="custom_form_label is_required">Note:</label>

                                            {dataState === 'Add' && referenceData && referenceData?.length > 0 && (
                                                <button className="add_data_btn p-ripple" onClick={handleAddReference}>
                                                    <i className="bi bi-plus-circle"></i>
                                                    Add reference
                                                    <Ripple />
                                                </button>
                                            )}
                                        </div>

                                        <InputTextarea
                                            id="note"
                                            value={note}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                                            className="custom_form_input is_text_area"
                                            placeholder="Note"
                                            autoComplete="off"
                                        />

                                        {noteError && <small className="form_error_msg">{noteError}</small>}
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="noteStatus" className="custom_form_label is_required">Status</label>

                                        <div className="response_status_container px-0">
                                            <div className="response_status_grp">
                                                <RadioButton
                                                    inputId="Satisfactory"
                                                    value={noteStatus}
                                                    onChange={(e: RadioButtonChangeEvent) => setNoteStatus("Satisfactory")
                                                    }
                                                    checked={noteStatus === "Satisfactory"}
                                                />
                                                <label htmlFor="Satisfactory">
                                                    Satisfactory
                                                </label>
                                            </div>
                                            <div className="response_status_grp">
                                                <RadioButton
                                                    inputId="Unsatisfactory"
                                                    value={noteStatus}
                                                    onChange={(e: RadioButtonChangeEvent) =>
                                                        setNoteStatus("Unsatisfactory")
                                                    }
                                                    checked={noteStatus === "Unsatisfactory"}
                                                />
                                                <label htmlFor="Unsatisfactory">
                                                    Unsatisfactory
                                                </label>
                                            </div>
                                        </div>

                                        {noteStatusError &&
                                            <small className="form_error_msg">{noteStatusError}</small>
                                        }
                                    </div>
                                </div>

                                {/* Files */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="noteFiles" className="custom_form_label">Files</label>

                                        <div className="checklist_img_container mt-2 mb-1">
                                            {dataState === 'Edit' && (
                                                <React.Fragment>
                                                    {selectedNoteData?.response_building_inspector && selectedNoteData?.response_building_inspector !== null && (
                                                        <React.Fragment>
                                                            {selectedNoteData?.response_building_inspector?.file_list && selectedNoteData?.response_building_inspector?.file_list?.length > 0 && (
                                                                <React.Fragment>
                                                                    {selectedNoteData?.response_building_inspector?.file_list?.map((file, index) => (
                                                                        <div className="note_image_container response_image_container" key={file?.id}>
                                                                            {noteFileDeleteLoadingId === file?.id ? (
                                                                                <FileActionLoader />
                                                                            ) : (
                                                                                <React.Fragment>
                                                                                    {file?.extension === "pdf" ? (
                                                                                        <div
                                                                                            className="response_doc_file_container"
                                                                                            onClick={() => handleViewDocument(file?.path!)}
                                                                                        >
                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <Image
                                                                                            src={file?.path!}
                                                                                            alt={`${index + 1}`}
                                                                                            className="note_img response_image"
                                                                                            preview
                                                                                        />
                                                                                    )}
                                                                                </React.Fragment>
                                                                            )}

                                                                            <button
                                                                                className="img_clear_btn p-ripple"
                                                                                onClick={() => handleDeleteNoteFile(file?.id!)}
                                                                            >
                                                                                <i className="bi bi-x"></i>
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </React.Fragment>
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            )}

                                            {noteFiles && noteFiles?.length > 0 && (
                                                <React.Fragment>
                                                    {noteFiles.map((file, index) => {
                                                        const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                        return (
                                                            <div
                                                                className="note_image_container response_image_container"
                                                                key={index}
                                                            >
                                                                {fileExtension === "pdf" ? (
                                                                    <div
                                                                        className="response_doc_file_container"
                                                                        onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                    </div>
                                                                ) : (
                                                                    <Image
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`${index + 1}`}
                                                                        className="note_img response_image"
                                                                        preview
                                                                    />
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleRemoveNoteFile(index)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            )}

                                            <Tooltip target={`.img_upload_label`} />

                                            <label
                                                htmlFor="noteFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="noteFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleNoteFilesUpload}
                                                />
                                                <Ripple />
                                            </label>
                                        </div>

                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>

                                        {noteFilesError &&
                                            <small className="form_error_msg">{noteFilesError}</small>
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Note response modal */}
            <Dialog
                visible={showArchitectResponseModal}
                headerClassName="custom_modal_header"
                header={responseModalHeader}
                footer={responseModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handeleCloseArcResponseModal}>
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {selectedNoteData && selectedNoteData !== null && (
                            <div className="row">
                                {/* Item */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="biNote" className={`custom_form_label`}>Inspection: </label>
                                        <div className="data_label flex-column align-items-start">
                                            <span className="text-capitalize text-dark pb-1 w-100">{activeSiteInspectionData?.inspection_name}:</span>
                                            <span className="w-100">
                                                <span className="text-secondary">{selectedNoteData?.note_no}.&nbsp;</span>
                                                {selectedNoteData?.note}
                                            </span>

                                            {selectedNoteData?.response_building_inspector &&
                                                selectedNoteData?.response_building_inspector?.file_list &&
                                                selectedNoteData?.response_building_inspector?.file_list?.length > 0 && (
                                                    <div className="note_files_area mb-1">
                                                        <div className="checklist_img_container custom">
                                                            {selectedNoteData?.response_building_inspector?.file_list?.map((file, index) => (
                                                                <div
                                                                    className="note_image_container sm response_image_container"
                                                                    key={file?.id}
                                                                >
                                                                    {file?.extension === "pdf" ? (
                                                                        <div
                                                                            className="response_doc_file_container"
                                                                            onClick={() => handleViewDocument(file?.path!)}
                                                                        >
                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                        </div>
                                                                    ) : (
                                                                        <Image
                                                                            src={file?.path!}
                                                                            alt={`${index + 1}`}
                                                                            className="note_img response_image"
                                                                            preview
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                {/* Response note */}
                                <div className="col-12">
                                    <Textarea
                                        id="arcResponseNote"
                                        label="Response note"
                                        labelHtmlFor="arcResponseNote"
                                        required={true}
                                        value={arcResponseNote}
                                        placeholder="Enter response note"
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setArcResponseNote(e.target.value)}
                                        error={arcResponseNoteError}
                                    />
                                </div>

                                {/* Response Files */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="" className="custom_form_label">Files</label>

                                        <div className="checklist_img_container mt-2 mb-1">
                                            {dataState === 'Edit' && (
                                                <React.Fragment>
                                                    {selectedNoteData?.response_architect?.file_list && selectedNoteData?.response_architect?.file_list?.length > 0 && (
                                                        <React.Fragment>
                                                            {selectedNoteData?.response_architect?.file_list?.map((file, index) => (
                                                                <div className="note_image_container response_image_container" key={file?.id}>
                                                                    {responseFileDeleteLoadingId === file?.id ? (
                                                                        <FileActionLoader />
                                                                    ) : (
                                                                        <React.Fragment>
                                                                            {file?.extension === "pdf" ? (
                                                                                <div
                                                                                    className="response_doc_file_container"
                                                                                    onClick={() => handleViewDocument(file?.path!)}
                                                                                >
                                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                                </div>
                                                                            ) : (
                                                                                <Image
                                                                                    src={file?.path!}
                                                                                    alt={`${index + 1}`}
                                                                                    className="note_img response_image"
                                                                                    preview
                                                                                />
                                                                            )}
                                                                        </React.Fragment>
                                                                    )}

                                                                    <button
                                                                        className="img_clear_btn p-ripple"
                                                                        onClick={() => handleDeleteResponseFile(file?.id!)}
                                                                    >
                                                                        <i className="bi bi-x"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            )}

                                            {arcResponseFiles && arcResponseFiles?.length > 0 && (
                                                <React.Fragment>
                                                    {arcResponseFiles.map((file, index) => {
                                                        const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                        return (
                                                            <div
                                                                className="note_image_container response_image_container"
                                                                key={index}
                                                            >
                                                                {fileExtension === "pdf" ? (
                                                                    <div
                                                                        className="response_doc_file_container"
                                                                        onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                    </div>
                                                                ) : (
                                                                    <Image
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`${index + 1}`}
                                                                        className="note_img response_image"
                                                                        preview
                                                                    />
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleRemoveResponseFile(index)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            )}

                                            <Tooltip target={`.img_upload_label`} />

                                            <label
                                                htmlFor="arcResponseFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="arcResponseFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleResponseFilesUpload}
                                                />
                                                <Ripple />
                                            </label>
                                        </div>

                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>

                                        {arcResponseFilesError &&
                                            <small className="form_error_msg">{arcResponseFilesError}</small>
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Note reference modal */}
            <Dialog
                visible={showReferenceModal}
                header={referenceModalHeader}
                footer={showMultipleSelection ? referenceModalFooter : null}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseReferenceModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="d-flex justify-content-between gap-2">
                                    <h6 className="selection_info_text">
                                        {showMultipleSelection && (
                                            <React.Fragment>
                                                {selectedReferenceList?.length} reference{selectedReferenceList?.length > 1 ? 's' : ''} selected
                                            </React.Fragment>
                                        )}
                                    </h6>

                                    {havePermissionAddSiteVisitQuestion && (
                                        <button className={`add_data_btn ${showMultipleSelection ? 'danger' : ''} p-ripple`} onClick={handleToggleAddMultipleQuestions}>
                                            <i className={`bi ${showMultipleSelection ? 'bi-dash-circle' : 'bi-plus-circle'}`}></i>
                                            {showMultipleSelection ? 'Cancel' : 'Add multiple references'}
                                            <Ripple />
                                        </button>
                                    )}
                                </div>
                                <hr />
                            </div>

                            {referenceData && referenceData?.length > 0 ? (
                                <div className="col-12">
                                    <ul className="reference_list_area">
                                        {referenceData?.map((reference, index) => {
                                            const isSelected = selectedReferenceList.some(
                                                (item) => item.id === reference.id
                                            );

                                            const isReferenceUsed = selectedSiteInspectionData?.note_list?.some(note => note?.reference_id === reference?.id);

                                            return (
                                                <li
                                                    key={reference?.id}
                                                    className="reference_list_item"
                                                >
                                                    <div className="reference_item_grp">
                                                        <p>
                                                            <span className="text-dark">{index + 1}.&nbsp;</span>
                                                            {reference?.inspection_reference}
                                                        </p>
                                                        {isReferenceUsed && (
                                                            <span className="reference_info success">Already used</span>
                                                        )}
                                                    </div>

                                                    {showMultipleSelection ? (
                                                        <Checkbox
                                                            inputId={`reference_${reference?.id}`}
                                                            checked={isSelected}
                                                            onChange={(e: CheckboxChangeEvent) => {
                                                                if (e.checked) {
                                                                    setSelectedReferenceList((prev) => [...prev, reference]);
                                                                } else {
                                                                    setSelectedReferenceList((prev) =>
                                                                        prev.filter((item) => item.id !== reference.id)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <button
                                                            className="ref_select_btn p-ripple"
                                                            onClick={() => handleSelectReference(reference)}
                                                        >
                                                            {(reference?.id === selectedReferenceData?.id || isReferenceUsed) ? 'Re-Select' : 'Select'}
                                                            <Ripple />
                                                        </button>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            ) : (
                                <div className="col-12">
                                    <div className="feature_not_available_area mb-0">
                                        <h6>No references available.</h6>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                {emailAddresses.map((email, index) => (
                                    <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                        <TextInput
                                            id={`emailAddress-${index}`}
                                            label={`E-mail address ${index + 1}`}
                                            labelHtmlFor={`emailAddress-${index}`}
                                            required={true}
                                            value={email.value}
                                            placeholder="abcd@gmail.com"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleEmailChange(index, e.target.value)
                                            }
                                            error={email.error}
                                            keyFilter={'email'}
                                            formGroupClassName="mb-1"
                                        />
                                        {emailAddresses.length > 1 && (
                                            <i
                                                className="bi bi-dash-circle mail_remove_btn"
                                                title="Remove"
                                                onClick={() => handleRemoveMail(index)}
                                            ></i>
                                        )}
                                    </div>
                                ))}

                                <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                    <i className={`bi bi-plus-circle`}></i>
                                    Add mail
                                    < Ripple />
                                </button>
                            </div>

                            <div className="col-12 mt-3">
                                {siteVisitData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Site vist report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showNoteDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}
        </React.Fragment >
    )
}

export default SiteVisitDetail;